import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { sortOptionsConfig, useFilters } from '@atlassian/mpac-primitives';
import { useCallback, useEffect, useState } from 'react';
import { defaultFilterDefinitions } from '../utils/filters/searchPageFiltersConfig';
import { getSortFilter } from '../utils/UrlMapUtils';
import { useCommitOptions, useFetchFilters, useSortFilter } from './subHooks';
import { useSearchPageAnalytics } from './useSearchPageAnalytics';
import { useSyncQueryParamsWithFilters } from './useSyncQueryParamsWithFilters';
// eslint-disable-next-line max-lines-per-function
export const useSearchFilters = function useSearchFilters() {
  let allFilterDefinitions = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : defaultFilterDefinitions;
  let source = arguments.length > 1 ? arguments[1] : undefined;
  // UI Filters Setup
  const {
    filterOptions,
    setFilterOptions,
    selectedOptions,
    setSelectedOptions,
    clearFilter,
    clearAllFilters,
    showAllFilters,
    setShowAllFilters,
    filterStatus,
    setFilterStatus
  } = useFilters({
    defaultFilterOptions: {},
    defaultShowAllFilters: source === 'PRODUCT'
  });

  /**
   * Committed Options for filters
   * @summary
   * 1. selectedOptions: Options that are selected by the user on UI
   * 2. commitOptions: Options that are committed to the URL and fetched
   *
   * on Desktop, they both are kept in sync.
   *
   * on Mobile, selectedOptions are used for UI changes and
   * commitOptions are used and updated when 'apply' is clicked.
   */
  const {
    commitOptions,
    setCommitOptions
  } = useCommitOptions(filterOptions, setSelectedOptions, setShowAllFilters);

  /**
   * Sorting Options
   * @summary
   * 1. commitSortKey: Sort key that are committed to the URL and applied
   * 1. selectedMobileSortOption: Sort Option used only in Mobile dialog that is selected by the user
   *
   * on Desktop, only commitSortKey is used.
   *
   * on Mobile, selectedMobileSortOption is used for UI changes and
   * commitSortKey is used and updated when 'apply' is clicked.
   */
  const {
    commitSortKey,
    setCommitSortKey,
    onCommitSortOptionSelect,
    selectedMobileSortOption,
    setSelectedMobileSortOption
  } = useSortFilter();
  const {
    handleFilterErrorStateAnalytics,
    handleFilterSelectionAnalytics,
    handleClearSelectionAnalytics,
    handleClearAllFiltersAnalytics,
    handleApplyFiltersClickAnalytics
  } = useSearchPageAnalytics();

  // Functions to handle changes in URL params
  const {
    setQueryParams,
    clearFilterFromQueryParam
  } = useSyncQueryParamsWithFilters({
    filterOptions
  });

  // ----------------------------------------------------------------------------------------
  // Fetching Filter Options from backend Section

  const [isFiltersFetchPending, setIsFiltersFetchPending] = useState(true);

  // Fetching the filter options and status from DLA
  const {
    currentFilterOptions,
    currentFilterState
  } = useFetchFilters(allFilterDefinitions, setIsFiltersFetchPending);

  // Updating the filter status and options when they are loaded from DLA
  useEffect(() => {
    setFilterStatus(currentFilterState);
    setFilterOptions(currentFilterOptions);
  }, [setFilterStatus, setFilterOptions, currentFilterState, currentFilterOptions]);
  useEffect(() => {
    // HANDLE ANALYTICS FOR FILTER OPTIONS ERROR
    if (!isFiltersFetchPending) {
      handleFilterErrorStateAnalytics({
        currentFilterState
      });
    }
  }, [isFiltersFetchPending]);

  // ----------------------------------------------------------------------------------------

  // Callback function that handles handle changes in selected options
  const onSelectedOptionsChange = useCallback(_ref => {
    let {
      filterId,
      options,
      location
    } = _ref;
    const newOptions = _objectSpread(_objectSpread({}, selectedOptions), {}, {
      [filterId]: options
    });
    setSelectedOptions(newOptions);
    if (location !== 'mobile') {
      // commit to option changes on desktop
      setQueryParams(newOptions, commitSortKey);
      handleFilterSelectionAnalytics({
        filterType: filterId,
        filterValue: newOptions[filterId]
      });
    }
  }, [selectedOptions, setSelectedOptions, setQueryParams, commitSortKey]);

  // to handle clearing of all filters when 'clear filters' button within filters is clicked
  const onClearAllFilters = useCallback(_ref2 => {
    let {
      location
    } = _ref2;
    clearAllFilters();
    if (location === 'desktop') {
      // not clearing sort key as it is not part of filters on desktop
      setQueryParams({}, commitSortKey);
      handleClearAllFiltersAnalytics({
        filters: selectedOptions
      });
    }
    if (location === 'mobile') {
      // resetting mobile sort option to default when clearing filters on mobile
      setSelectedMobileSortOption(sortOptionsConfig[0]);
    }
  }, [clearAllFilters, commitSortKey, setQueryParams, setSelectedMobileSortOption]);

  // to handle clearing of individual filter
  const onClearFilter = useCallback(id => {
    clearFilter(id);
    clearFilterFromQueryParam(id);
    handleClearSelectionAnalytics({
      filterType: id
    });
  }, [clearFilter, clearFilterFromQueryParam]);

  // Function to handle closing of mobile dialog
  const onCloseMobileDialog = useCallback(() => {
    // reset selected options to committed options
    setSelectedOptions(commitOptions);
    setSelectedMobileSortOption(getSortFilter(commitSortKey) || selectedMobileSortOption);
  }, [commitOptions, commitSortKey, selectedMobileSortOption, setSelectedMobileSortOption, setSelectedOptions]);

  // to handle applying of filters in mobile dialog
  const onApplyFilters = useCallback(() => {
    setCommitSortKey(selectedMobileSortOption.value);
    setQueryParams(selectedOptions, selectedMobileSortOption.value);
    const filters = _objectSpread({
      sort: selectedMobileSortOption.label
    }, selectedOptions !== null && selectedOptions !== void 0 ? selectedOptions : {});
    handleApplyFiltersClickAnalytics({
      filters
    });
  }, [setCommitSortKey, selectedMobileSortOption.value, setQueryParams, selectedOptions]);

  // Function to handle changes in selected sort option
  const onSelectedSortOptionChange = useCallback(_ref3 => {
    let {
      option
    } = _ref3;
    return setSelectedMobileSortOption(option);
  }, [setSelectedMobileSortOption]);

  // ----------------------------------------------------------------------------------------

  // Function to handle clearing of filters when no hits are found and 'clear filters' button is clicked on page level
  const handleClearFilters = useCallback(() => {
    clearAllFilters();
    setSelectedMobileSortOption(sortOptionsConfig[0]);
    setQueryParams({});
  }, [clearAllFilters, setSelectedMobileSortOption, setQueryParams]);
  return {
    allFilterDefinitions,
    filterOptions,
    sortOptions: sortOptionsConfig,
    selectedOptions,
    commitOptions,
    commitSortKey,
    selectedMobileSortOption,
    isFiltersFetchPending,
    filterStatus,
    showAllFilters,
    onSelectedOptionsChange,
    onClearFilter,
    onClearAllFilters,
    onApplyFilters,
    onSelectedSortOptionChange,
    onCloseMobileDialog,
    onCommitSortOptionSelect,
    handleClearFilters,
    setShowAllFilters,
    setCommitOptions
  };
};